import { useDeferredValue, useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../lib/auth-context'
import { api, type TenantSearchResult } from '../lib/api'
import { clearSupportContext, useSupportReadOnlyView, writeSupportContext } from '../lib/support-session'

function formatDateTime(value?: string | null) {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString()
}

function formatTenantSummary(result: TenantSearchResult) {
  const pieces = [result.tenantType, result.tenantStatus].filter(Boolean)
  return pieces.length ? pieces.join(' • ') : 'Tenant match'
}

function formatTenantReasons(result: TenantSearchResult) {
  return result.matchReasons.length ? result.matchReasons.join(' • ') : 'No match detail available'
}

export default function PlatformOwnerHomePage() {
  const { user } = useAuth()
  const { supportSession } = useSupportReadOnlyView()
  const [selectedTenant, setSelectedTenant] = useState<TenantSearchResult | null>(null)
  const [tenantQuery, setTenantQuery] = useState('')
  const deferredTenantQuery = useDeferredValue(tenantQuery)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [supportReason, setSupportReason] = useState('')
  const [endReason, setEndReason] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [startingSupport, setStartingSupport] = useState(false)
  const [endingSupport, setEndingSupport] = useState(false)

  const supportSessionQuery = useQuery({
    queryKey: ['platform-support-current-session'],
    queryFn: () => api.platformSupport.currentSession().then((res) => res.data.supportSession),
  })

  useEffect(() => {
    if (!supportSessionQuery.isSuccess) return
    const session = supportSessionQuery.data
    if (!session) {
      clearSupportContext()
      return
    }
    writeSupportContext(session)
  }, [supportSessionQuery.data, supportSessionQuery.isSuccess])

  const searchTerm = deferredTenantQuery.trim()
  const tenantSearchQuery = useQuery({
    queryKey: ['platform-tenant-search', searchTerm],
    queryFn: () => api.platformSupport.searchTenants(searchTerm).then((res) => res.data),
    enabled: searchTerm.length > 0,
  })

  const tenantResults = tenantSearchQuery.data?.results ?? []
  const hasSearchResults = tenantResults.length > 0
  const activeOption = tenantResults[highlightedIndex] ?? null
  const canStartSupport = Boolean(selectedTenant) && Boolean(supportReason.trim()) && !supportSession
  const canEndSupport = Boolean(supportSession?.sessionId) && Boolean(endReason.trim())

  useEffect(() => {
    if (!searchTerm) {
      setIsPickerOpen(false)
      setHighlightedIndex(0)
      return
    }
    setHighlightedIndex(0)
  }, [searchTerm])

  const handleSelectTenant = (tenant: TenantSearchResult) => {
    setSelectedTenant(tenant)
    setTenantQuery(tenant.tenantName)
    setIsPickerOpen(false)
    setHighlightedIndex(0)
    setFormError(null)
    setSuccessMessage(null)
  }

  const handleTenantKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isPickerOpen || !tenantResults.length) {
      if (event.key === 'ArrowDown' && tenantResults.length) {
        event.preventDefault()
        setIsPickerOpen(true)
      }
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedIndex((current) => (current + 1) % tenantResults.length)
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedIndex((current) => (current - 1 + tenantResults.length) % tenantResults.length)
      return
    }

    if (event.key === 'Enter' && activeOption) {
      event.preventDefault()
      handleSelectTenant(activeOption)
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      setIsPickerOpen(false)
    }
  }

  const startSupportSession = async (event: FormEvent) => {
    event.preventDefault()
    setFormError(null)
    setSuccessMessage(null)

    if (!selectedTenant) {
      setFormError('Choose a tenant before starting support.')
      return
    }

    const reason = supportReason.trim()
    if (!reason) {
      setFormError('Support reason is required.')
      return
    }

    try {
      setStartingSupport(true)
      const response = await api.platformSupport.startSession({
        targetTenantOrgId: selectedTenant.tenantId,
        reason,
      })
      writeSupportContext(response.data.supportSession)
      setSuccessMessage(`Support session started for ${selectedTenant.tenantName}.`)
      setEndReason('')
    } catch (error: any) {
      setFormError(error?.response?.data?.error ?? 'Failed to start support session.')
    } finally {
      setStartingSupport(false)
    }
  }

  const endSupportSession = async () => {
    setFormError(null)
    setSuccessMessage(null)

    const sessionId = supportSession?.sessionId
    const reason = endReason.trim()
    if (!sessionId) {
      setFormError('No active support session is available to end.')
      return
    }
    if (!reason) {
      setFormError('End session reason is required.')
      return
    }

    try {
      setEndingSupport(true)
      await api.platformSupport.endSession(sessionId, reason)
      clearSupportContext()
      setSuccessMessage('Support session ended.')
      setEndReason('')
    } catch (error: any) {
      setFormError(error?.response?.data?.error ?? 'Failed to end support session.')
    } finally {
      setEndingSupport(false)
    }
  }

  const activeSupportTenantName = useMemo(() => {
    if (!supportSession?.targetTenantOrgId) return 'No active support session'
    if (selectedTenant?.tenantId === supportSession.targetTenantOrgId) return selectedTenant.tenantName
    return supportSession.targetTenantOrgId
  }, [selectedTenant, supportSession])

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-8">
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Diamond Dispatch</div>
        <h1 className="mt-2 text-2xl font-bold text-slate-900">Platform Owner Control Home</h1>
        <p className="mt-2 text-sm text-slate-600">
          Powered by AUX. This home is scoped for sandbox oversight, support-session visibility, and multi-company workflow verification.
        </p>
      </div>

      {successMessage && (
        <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {successMessage}
        </div>
      )}
      {formError && (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          {formError}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Account</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Name</dt>
              <dd className="font-medium text-slate-900">{user?.name ?? 'Unknown'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Email</dt>
              <dd className="font-medium text-slate-900">{user?.email ?? 'Unknown'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Role</dt>
              <dd className="font-medium text-slate-900">{user?.accountType ?? 'Unknown'}</dd>
            </div>
          </dl>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Active Support View</h2>
          {!supportSession && (
            <p className="mt-3 text-sm text-slate-600">No active support session context stored in this browser session.</p>
          )}
          {supportSession && (
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Session</dt>
                <dd className="font-medium text-slate-900">{supportSession.sessionId ?? 'N/A'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Target Tenant</dt>
                <dd className="font-medium text-slate-900">{supportSession.targetTenantOrgId ?? 'N/A'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Reason</dt>
                <dd className="font-medium text-slate-900">{supportSession.reason ?? 'N/A'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Started</dt>
                <dd className="font-medium text-slate-900">{formatDateTime(supportSession.startedAt)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Expires</dt>
                <dd className="font-medium text-slate-900">{formatDateTime(supportSession.expiresAt)}</dd>
              </div>
            </dl>
          )}
        </section>
      </div>

      <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Support Session Controls</h2>
            <p className="mt-1 text-sm text-slate-600">
              Search by tenant name, user name or email, VIN, order reference, load number, or trip reference.
            </p>
          </div>
          {supportSession?.sessionId && (
            <div className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900">
              Support active for {activeSupportTenantName}
            </div>
          )}
        </div>

        <form onSubmit={startSupportSession} className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
          <div className="relative">
            <label htmlFor="tenant-search" className="mb-1 block text-sm font-medium text-slate-700">
              Choose tenant
            </label>
            <input
              id="tenant-search"
              type="text"
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={isPickerOpen}
              aria-controls="tenant-search-results"
              aria-activedescendant={activeOption ? `tenant-option-${activeOption.tenantId}` : undefined}
              value={tenantQuery}
              onChange={(event) => {
                setTenantQuery(event.target.value)
                setSelectedTenant(null)
                setFormError(null)
                setSuccessMessage(null)
                setIsPickerOpen(true)
              }}
              onFocus={() => {
                if (tenantQuery.trim()) setIsPickerOpen(true)
              }}
              onBlur={() => {
                window.setTimeout(() => setIsPickerOpen(false), 150)
              }}
              onKeyDown={handleTenantKeyDown}
              placeholder="Search by company, user, VIN, order, load, or trip"
              className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm text-slate-900 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
            />

            {selectedTenant && (
              <div className="mt-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-slate-800">
                <div className="font-semibold text-slate-900">Selected tenant</div>
                <div className="mt-1">{selectedTenant.tenantName}</div>
                <div className="text-xs text-slate-600">{formatTenantSummary(selectedTenant)}</div>
                <div className="text-xs text-slate-500">Technical ID: {selectedTenant.technicalTenantId}</div>
              </div>
            )}

            {isPickerOpen && searchTerm && (
              <div
                id="tenant-search-results"
                role="listbox"
                aria-label="Tenant search results"
                className="absolute z-20 mt-2 max-h-96 w-full overflow-auto rounded-2xl border border-slate-200 bg-white shadow-lg"
              >
                {tenantSearchQuery.isLoading && (
                  <div className="px-4 py-5 text-sm text-slate-500">Searching tenants...</div>
                )}
                {tenantSearchQuery.isError && (
                  <div className="px-4 py-5 text-sm text-rose-600">Tenant search failed. Please try again.</div>
                )}
                {tenantSearchQuery.isSuccess && !hasSearchResults && (
                  <div className="px-4 py-5 text-sm text-slate-500">No tenants matched your search.</div>
                )}
                {tenantSearchQuery.isSuccess && hasSearchResults && tenantResults.map((tenant, index) => {
                  const isHighlighted = index === highlightedIndex
                  const isSelected = selectedTenant?.tenantId === tenant.tenantId
                  return (
                    <button
                      key={tenant.tenantId}
                      id={`tenant-option-${tenant.tenantId}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        handleSelectTenant(tenant)
                      }}
                      className={`block w-full border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 ${
                        isHighlighted ? 'bg-cyan-50' : 'bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-slate-900">{tenant.tenantName}</div>
                          <div className="mt-1 text-xs text-slate-600">{formatTenantSummary(tenant)}</div>
                          <div className="mt-1 text-xs text-slate-500">{formatTenantReasons(tenant)}</div>
                        </div>
                        <div className="text-right text-[11px] uppercase tracking-wide text-slate-400">
                          <div>{tenant.matchedSources.join(' • ')}</div>
                          <div className="mt-1 text-slate-500">ID {tenant.technicalTenantId.slice(0, 8)}</div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="support-reason" className="mb-1 block text-sm font-medium text-slate-700">
                Support reason
              </label>
              <textarea
                id="support-reason"
                value={supportReason}
                onChange={(event) => setSupportReason(event.target.value)}
                rows={4}
                required
                placeholder="Explain why this support session is needed"
                className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm text-slate-900 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
              />
            </div>

            <div>
              <label htmlFor="end-reason" className="mb-1 block text-sm font-medium text-slate-700">
                End session reason
              </label>
              <input
                id="end-reason"
                type="text"
                value={endReason}
                onChange={(event) => setEndReason(event.target.value)}
                placeholder="Why is this support session ending?"
                className="w-full rounded-xl border border-slate-300 px-3 py-3 text-sm text-slate-900 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={!canStartSupport || startingSupport}
                className="rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {startingSupport ? 'Starting support session...' : 'Start support session'}
              </button>
              <button
                type="button"
                disabled={!canEndSupport || endingSupport}
                onClick={endSupportSession}
                className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {endingSupport ? 'Ending support session...' : 'End Support Session'}
              </button>
            </div>

            <p className="text-xs text-slate-500">
              The picker returns the exact tenant ID expected by the support-session API. Keyboard users can move through the results with the arrow keys and press Enter to select.
            </p>
          </div>
        </form>
      </section>
    </div>
  )
}
